class ProductBuyBar extends HTMLElement {
  constructor() {
    "use strict";
    super();

    this.sectionId = this.dataset.section;
    this.productId = this.dataset.product;
    this.classes = { show: "show" };
    this.mainProductForm = document.querySelector(".product-form");
    
    // Get variants data
    const variantsScript = document.getElementById(`buy-bar-variants-${this.sectionId}`);
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];

    this.initIntersectionObserver();
    this.initSizeButtons();
    this.initChangeButton();
    this.initMainVariantSync();
    
    // Prevent UI "flicker" when switching variants:
    // When a customer clicks a size, the main product section updates asynchronously.
    // During that short window, our periodic sync could still read the *old* variant
    // and temporarily snap the selection back.
    this.pendingSelection = null; // { optionIndex: number, value: string, until: number }
    this.currentVariantId = null; // last variant id we synced to (used to avoid unnecessary updates)
    this._variantSyncPoller = null; // timeout id for one-shot polling

    // Initial sync
    this.syncFromMainPicker();
  }

  // Get fresh DOM references each time (handles DOM replacements)
  get form() {
    return this.querySelector("form");
  }
  
  get variantInput() {
    return this.querySelector('input[name="id"]');
  }
  
  get submitButton() {
    return this.querySelector(".buy-bar__submit");
  }
  
  get submitText() {
    return this.querySelector(".buy-bar__submit-text");
  }
  
  get submitPrice() {
    return this.querySelector(".buy-bar__submit-price");
  }
  
  get sizeButtons() {
    return this.querySelectorAll(".buy-bar__size-btn");
  }

  initIntersectionObserver() {
    // Lilja (2.9.2026): the bar must only appear once the main Add-to-cart form has been scrolled
    // PAST (it sits above the viewport), never while it is still further down the page. The old
    // check ("not intersecting and scrollY > 200") also fired for the footer's initial callback and
    // for the form being below the fold, which made the bar pop up early, hide, then come back.
    const show = () => {
      this.classList.add(this.classes.show);
      document.body.classList.add('sticky-buy-bar-visible');
    };
    const hide = () => {
      this.classList.remove(this.classes.show);
      document.body.classList.remove('sticky-buy-bar-visible');
    };
    const formScrolledPast = () => {
      if (!this.mainProductForm) return false;
      return this.mainProductForm.getBoundingClientRect().bottom < 0;
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            hide();
          } else if (formScrolledPast()) {
            show();
          }
        });
      },
      { threshold: 0.25 }
    );

    if (this.mainProductForm) {
      observer.observe(this.mainProductForm);
    }

    // Observe footer after DOM ready
    document.addEventListener("DOMContentLoaded", () => {
      const siteFooter = document.querySelector("footer.footer");
      if (siteFooter) {
        observer.observe(siteFooter);
      }
    });
  }

  initSizeButtons() {
    // Use event delegation on the buy bar itself (doesn't get replaced)
    this.addEventListener("click", (e) => {
      const btn = e.target.closest(".buy-bar__size-btn");
      if (!btn) return;
      
      e.preventDefault();
      if (btn.getAttribute("aria-disabled") === "true") return;

      const value = btn.dataset.value;
      const optionIndex = parseInt(btn.dataset.optionIndex, 10);

      // Update UI immediately
      this.sizeButtons.forEach((b) => b.classList.remove("is-selected"));
      btn.classList.add("is-selected");

      // Hold this selection briefly to avoid snapping back while the main picker updates.
      this.setPendingSelection(optionIndex, value);

      // Update main variant picker
      this.updateMainVariantPicker(optionIndex, value);
    });
  }

  setPendingSelection(optionIndex, value) {
    // Keep it short so it doesn't feel "sticky" if the theme update is fast.
    this.pendingSelection = {
      optionIndex,
      value,
      until: Date.now() + 700
    };
  }

  clearPendingSelection() {
    this.pendingSelection = null;
  }

  pendingSelectionIsActive() {
    return !!(this.pendingSelection && Date.now() < this.pendingSelection.until);
  }

  variantMatchesPendingSelection(variant) {
    if (!variant || !this.pendingSelection) return false;
    const { optionIndex, value } = this.pendingSelection;
    const key = `option${optionIndex + 1}`;
    return variant[key] === value;
  }

  initChangeButton() {
    // Handle "Change" button click - scroll to variant picker
    this.addEventListener("click", (e) => {
      const changeBtn = e.target.closest("[data-scroll-to-variants]");
      if (!changeBtn) return;
      
      e.preventDefault();
      
      // Find the variant picker on the page
      const variantPicker = document.querySelector(
        `variant-selects[data-section="${this.sectionId}"]`
      );
      
      if (variantPicker) {
        // Hide the sticky bar temporarily
        this.classList.remove(this.classes.show);
        
        // Scroll to variant picker with offset for header
        const headerHeight = document.querySelector('header')?.offsetHeight || 80;
        const targetPosition = variantPicker.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        // Focus the first radio/select for accessibility
        setTimeout(() => {
          const firstInput = variantPicker.querySelector('input[type="radio"], select');
          if (firstInput) {
            firstInput.focus();
          }
        }, 500);
      }
    });
  }

  initMainVariantSync() {
    // Use event delegation at document level to catch all variant changes
    // This works even when DOM elements are replaced
    document.addEventListener('change', (e) => {
      // Check if the change is from a variant selector in our section
      const variantSelects = e.target.closest(`variant-selects[data-section="${this.sectionId}"]`);
      if (!variantSelects) return;
      
      // The theme updates product DOM asynchronously (fetch + replace).
      // Instead of a fixed delay (feels "tahmea"), poll briefly and sync
      // as soon as the selected variant in DOM actually changes.
      this.startVariantSyncPoll();
    });

    // Also watch for ajaxProduct:added event to re-sync after cart add
    document.addEventListener('ajaxProduct:added', () => {
      setTimeout(() => this.syncFromMainPicker(), 100);
    });
  }

  startVariantSyncPoll() {
    // Avoid stacking multiple pollers if multiple change events fire quickly.
    if (this._variantSyncPoller) return;

    const startedAt = Date.now();
    const deadline = startedAt + 1100; // max time we keep polling

    const tick = () => {
      const v = this.getCurrentVariantFromDOM();

      // If DOM has moved to a new variant id, sync immediately.
      if (v && v.id && v.id !== this.currentVariantId) {
        this._variantSyncPoller = null;
        this.syncFromMainPicker();
        return;
      }

      // If the customer clicked a specific size, accept the sync once DOM matches it.
      if (this.pendingSelection && v && this.variantMatchesPendingSelection(v)) {
        this._variantSyncPoller = null;
        this.syncFromMainPicker();
        return;
      }

      if (Date.now() < deadline) {
        this._variantSyncPoller = setTimeout(tick, 50);
        return;
      }

      // Give up waiting and do one final sync attempt.
      this._variantSyncPoller = null;
      this.syncFromMainPicker();
    };

    this._variantSyncPoller = setTimeout(tick, 50);
  }

  getCurrentVariantFromDOM() {
    // Try to get variant from the data-selected-variant script
    const variantDataScript = document.querySelector(
      `variant-selects[data-section="${this.sectionId}"] [data-selected-variant]`
    );
    
    if (variantDataScript) {
      try {
        return JSON.parse(variantDataScript.textContent);
      } catch (err) {
        // Silent fail
      }
    }
    
    // Fallback: get from main form's hidden input
    const mainFormInput = document.querySelector(
      `form[id^="product-form-${this.sectionId}"] input[name="id"]`
    );
    
    if (mainFormInput && mainFormInput.value) {
      const variantId = parseInt(mainFormInput.value, 10);
      return this.variants.find(v => v.id === variantId);
    }
    
    return null;
  }

  syncFromMainPicker() {
    const variant = this.getCurrentVariantFromDOM();
    if (variant && variant.id) {
      this.currentVariantId = variant.id;
      this.updateVariant(variant);
      this.updateSizeButtonSelection(variant);

      // If the DOM has caught up to the size the customer clicked, remove the guard.
      if (this.pendingSelection && this.variantMatchesPendingSelection(variant)) {
        this.clearPendingSelection();
      }
    }
  }

  updateMainVariantPicker(optionIndex, value) {
    // Find the main variant picker (fresh reference)
    const mainPicker = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
    if (!mainPicker) return;

    // Try radio buttons first - look in the correct fieldset
    const fieldsets = mainPicker.querySelectorAll('fieldset');
    if (fieldsets[optionIndex]) {
      const radioInputs = fieldsets[optionIndex].querySelectorAll('input[type="radio"]');
      for (const radio of radioInputs) {
        if (radio.value === value) {
          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
          return;
        }
      }
    }

    // Try select dropdown
    const selectInputs = mainPicker.querySelectorAll("select");
    if (selectInputs[optionIndex]) {
      selectInputs[optionIndex].value = value;
      selectInputs[optionIndex].dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  updateVariant(variant) {
    if (!variant) return;

    // Update hidden input
    const variantInput = this.variantInput;
    if (variantInput) {
      variantInput.value = variant.id;
    }

    // Update button state
    const submitButton = this.submitButton;
    if (submitButton) {
      submitButton.disabled = !variant.available;
    }

    // Update button text
    const submitText = this.submitText;
    if (submitText) {
      if (variant.available) {
        const isPreorder = this.dataset.isPreorder === "true";
        submitText.textContent = isPreorder
          ? (window.variantStrings?.preorder || "Pre-order")
          : (window.variantStrings?.addToCart || "Lisää ostoskoriin");
      } else {
        submitText.textContent = window.variantStrings?.soldOut || "Loppuunmyyty";
      }
    }

    // Update price in button
    const submitPrice = this.submitPrice;
    if (submitPrice) {
      submitPrice.textContent = this.formatMoney(variant.price);
    }

    /**
     * IMPORTANT (Shopify multi-currency + formatting consistency):
     * We DO NOT rebuild the price HTML here.
     *
     * The theme already renders prices via Liquid (`snippets/price.liquid`) which:
     * - Uses Shopify-native `money` / `money_with_currency` filters
     * - Applies correct currency symbol when Market/country changes
     * - Includes compare-at (strikethrough) markup when on sale
     *
     * Rebuilding price markup in JS caused issues like "1300€" after switching to SEK
     * and comma/dot mismatches compared to the rest of the storefront.
     *
     * Price markup inside the sticky bar is kept in sync by the theme's variant AJAX
     * update (`VariantSelects.renderProductInfo()` updates `buy-bar-info-<sectionId>`).
     */
  }

  updateSizeButtonSelection(variant) {
    const sizeButtons = this.sizeButtons;
    if (!variant) return;

    // Get the size option value from the variant
    const sizeSelector = this.querySelector(".buy-bar__size-selector");
    const mobileVariant = this.querySelector(".buy-bar__mobile-variant");
    
    let optionIndex = 0;
    if (sizeSelector) {
      optionIndex = parseInt(sizeSelector.dataset.optionIndex, 10);
    } else if (mobileVariant) {
      const selectedSize = mobileVariant.querySelector(".buy-bar__selected-size");
      if (selectedSize) {
        optionIndex = parseInt(selectedSize.dataset.optionIndex, 10);
      }
    }

    const optionValue = variant[`option${optionIndex + 1}`];

    // Update desktop size buttons
    if (optionValue && sizeButtons.length) {
      sizeButtons.forEach((btn) => {
        const isSelected = btn.dataset.value === optionValue;
        btn.classList.toggle("is-selected", isSelected);
      });
    }

    // Update mobile selected size text
    if (optionValue && mobileVariant) {
      const selectedSizeEl = mobileVariant.querySelector(".buy-bar__selected-size");
      if (selectedSizeEl) {
        selectedSizeEl.textContent = optionValue;
      }
    }

    // Update availability states
    this.updateSizeButtonAvailability(optionIndex);
  }

  updateSizeButtonAvailability(sizeOptionIndex) {
    if (!this.variants.length) return;

    this.sizeButtons.forEach((btn) => {
      const sizeValue = btn.dataset.value;
      
      // Check if there's any available variant with this size value
      const hasAvailableVariant = this.variants.some((v) => {
        const variantSizeValue = v[`option${sizeOptionIndex + 1}`];
        return variantSizeValue === sizeValue && v.available;
      });

      btn.classList.toggle("is-unavailable", !hasAvailableVariant);
      btn.setAttribute("aria-disabled", !hasAvailableVariant ? "true" : "false");
    });
  }

  formatMoney(cents) {
    // Use theme-provided format (set in `layout/theme.liquid`) when available.
    const themeMoneyFormat =
      (window.theme && window.theme.settings && window.theme.settings.moneyFormat) ||
      null;

    if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
      // Shopify.formatMoney expects the "money format" string containing {{amount}} etc.
      return Shopify.formatMoney(cents, themeMoneyFormat || undefined);
    }

    // Fallback: keep it neutral (no hard-coded currency symbol).
    const amount = (cents / 100).toFixed(2);
    return amount;
  }
}

customElements.define("product-buy-bar", ProductBuyBar);


class UpsellCard extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select');
    this.image = this.querySelector('.card-media img');

    if (this.select) {
      this.select.addEventListener('change', (event) => {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const newImgSrc = selectedOption.getAttribute('data-variant-img-src');
        if (newImgSrc) { this.updateImage(newImgSrc) }
      });
    }
  }

  updateImage(newImgSrc) {
    if (this.image && newImgSrc) {
      const sanitized = String(newImgSrc).trim().replace(/[\s<>"']/g, '');

      const dangerousProtocols = /^(javascript|data|vbscript|file|about):/i;
      if (dangerousProtocols.test(sanitized)) {
        return;
      }

      try {
        const url = new URL(sanitized, window.location.origin);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          this.image.src = url.href;
        }
      } catch (e) {
        if (!sanitized.includes('javascript:') &&
            !sanitized.includes('data:') &&
            !sanitized.includes('vbscript:') &&
            !sanitized.includes('<') &&
            !sanitized.includes('>')) {
          this.image.src = sanitized;
        }
      }
    }
  }
}

customElements.define('upsell-card', UpsellCard);
